import { Component, OnDestroy, OnInit, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { finalize, first } from 'rxjs/operators';
import { AuthenticationService } from '../../../shared/services/api/authentication/authentication.service';
import { PasswordValidationService } from '../../../shared/services/password-validation.service';
import { UserSessionStore } from '../../../shared/services/user-session.store';
import { LoginHelperService } from '../../login/login-helper.service';
import { DtoUserSession, UserSession } from 'src/app/core/user/user.model';
import { ApiService } from '../../../shared/services/api/api.service';

const REDIRECT_DELAY = 5;

@Component({
  selector: 'app-create-password',
  templateUrl: './create-password.component.html',
  styleUrls: ['./create-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreatePasswordComponent implements OnInit, OnDestroy {
  readonly PASSWORD_REQUIREMENTS = this.passwordValidation.requirements;

  token: string | null = null;
  email = '';

  password = signal('');
  showPassword = signal(false);
  isSubmitting = signal(false);
  hasInteracted = signal(false);
  isExpired = signal(false);
  activationSuccess = signal(false);
  resendState: 'idle' | 'sending' | 'sent' = 'idle';
  cooldown = signal(0);
  redirectCountdown = signal(REDIRECT_DELAY);

  private cooldownInterval: ReturnType<typeof setInterval> | null = null;
  private resetTimeout: ReturnType<typeof setTimeout> | null = null;
  private redirectTimer: ReturnType<typeof setInterval> | null = null;
  private isLoggedIn = false;

  requirements = computed(() => this.passwordValidation.getRequirementsWithStatus(this.password()));

  allMet = computed(() => this.passwordValidation.allRequirementsMet(this.password()));

  strength = computed(() => this.passwordValidation.getPasswordStrength(this.password()));

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private passwordValidation: PasswordValidationService,
    private sessionStore: UserSessionStore,
    private loginHelper: LoginHelperService,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] ?? null;
      this.email = params['email'] ?? '';

      // Validate token
      if (this.token) {
        this.authenticationService.checkResetPasswordToken(this.token).subscribe(
          () => {},
          error => {
            const status = error?.status;
            switch (status) {
              // Error 403 - token is present but expired
              case 403: {
                this.router.navigate(['/login/reset-password-expired']).then();
                break;
              }
              // Error 404 (not found) / 500 or others - token is absent
              default: {
                this.router.navigate(['/login/reset-password-incorrect']).then();
                break;
              }
            }
          }
        );
      } else {
        this.router.navigate(['/login']).then();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
    if (this.resetTimeout) clearTimeout(this.resetTimeout);
    if (this.redirectTimer) clearInterval(this.redirectTimer);
  }

  onPasswordInput(value: string): void {
    this.password.set(value);
    if (!this.hasInteracted()) this.hasInteracted.set(true);
  }

  toggleShowPassword(): void {
    this.showPassword.update(v => !v);
  }

  onSubmit(): void {
    const pw = this.password();
    if (!this.allMet() || this.isSubmitting() || !this.token) return;

    this.isSubmitting.set(true);

    this.authenticationService
      .resetPassword(pw, this.token || '')
      .pipe(first())
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe(
        userSession => {
          if (userSession && typeof userSession === 'object') {
            this.sessionStore.setSession(new UserSession(userSession as DtoUserSession));
            this.apiService.exStatistics.setAccountInfos();
            this.apiService.exStatistics.setExplainStatistics();
            this.isLoggedIn = true;
          }
          this.activationSuccess.set(true);
          this.startRedirectCountdown();
        },
        error => {
          const status = error?.status;
          switch (status) {
            case 403:
            case 404: {
              this.isExpired.set(true);
              break;
            }
            default: {
              console.error('Une erreur est survenue...404 ou 500');
              break;
            }
          }
        }
      );
  }

  goToApp(): void {
    this.clearRedirectTimer();
    if (this.isLoggedIn) {
      this.loginHelper.navigateAfterLogin();
    } else {
      this.router.navigate(['/login']);
    }
  }

  goToLogin(): void {
    this.router.navigate(['/login']).then();
  }

  goBackToEmail(): void {
    this.router.navigate(['/login']).then();
  }

  private startRedirectCountdown(): void {
    this.redirectTimer = setInterval(() => {
      const next = this.redirectCountdown() - 1;
      this.redirectCountdown.set(next);
      if (next <= 0) {
        this.goToApp();
      }
    }, 1000);
  }

  private clearRedirectTimer(): void {
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
      this.redirectTimer = null;
    }
  }

  async handleResend(): Promise<void> {
    if (this.resendState === 'sending' || this.cooldown() > 0) return;
    if (!this.email) return;

    this.resendState = 'sending';
    try {
      await firstValueFrom(this.authenticationService.forgotPassword(this.email));
    } catch {
      // ignore
    }
    this.resendState = 'sent';
    this.cooldown.set(60);
    this.startCooldown();
    this.resetTimeout = setTimeout(() => {
      this.resendState = 'idle';
    }, 3000);
  }

  private startCooldown(): void {
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
    this.cooldownInterval = setInterval(() => {
      const next = this.cooldown() - 1;
      this.cooldown.set(next);
      if (next <= 0 && this.cooldownInterval) {
        clearInterval(this.cooldownInterval);
        this.cooldownInterval = null;
      }
    }, 1000);
  }
}
